/**
 * Sub-tab "Semanal Detalhado" do Controle PLD (Task #190 Fase 2).
 *
 * Tabela com todos os dias do mês: Data | Dia da semana | PLD diário | Revisão
 * (REV0-n) | status da revisão. Filtro por submercado vem do header da página.
 *
 * #224 (Veiga): no MÊS CORRENTE a tabela passa a mostrar a NOSSA PROJEÇÃO por dia
 * (mesmo valor do gráfico/projeção da Visão Geral, #219/#217) — dias futuros com
 * o PLD projetado, estilizados como estimativa (âmbar/itálico + tooltip). Mês
 * passado segue só realizado (comportamento atual).
 */
import { useMemo } from "react";
import type { PldSubmercado } from "../../../api/types";
import { usePldOverview } from "../../../hooks/usePldOverview";
import { usePldProjecao } from "../../../hooks/usePldProjecao";
import { usePldSemanas } from "../../../hooks/usePldSemanas";
import { PldEmpty, PldError, PldLoading } from "./PldStates";
import {
  SUBMERCADO_LABEL,
  diaSemanaBR,
  fmtDateBR,
  fmtPld,
  mesRefCorrente,
  statusBadge,
} from "./pldShared";

// Cor da projeção (âmbar = "futuro/estimado"), idêntica à Visão Geral (#209/#217).
const COR_PROJECAO = "#F5A623";

interface DiaLinha {
  data: string;
  pld: number | null;
  projetado: boolean; // true = dia ainda não realizado (valor = projeção)
}

interface Props {
  mesRef: string;
  submercado: PldSubmercado;
}

export function PldSemanalTab({ mesRef, submercado }: Props) {
  const overview = usePldOverview(mesRef, submercado);
  const semanas = usePldSemanas(mesRef, submercado);
  const isMesCorrente = mesRef === mesRefCorrente();
  const projecao = usePldProjecao(mesRef, submercado);

  // #38: o status do dia vem da SEMANA cuja janela [ini..fim] contém a data —
  // NÃO de um mapa por rótulo de revisão. Pós-#229 há duas linhas "REV0" (a do
  // mês corrente e a cross-month do mês seguinte), então mapear por rótulo
  // colidia e sobrescrevia o status do REV0 corrente pelo da cross-month
  // (dias 01–05/06 apareciam "Pendente" em vez de "Realizada"). A janela é
  // inequívoca (as semanas não se sobrepõem).

  // Fonte das linhas: no mês corrente, a projeção (#208) cobre o mês inteiro
  // (realizado + projetado pelo flag `realizado`); senão, só o realizado do
  // overview (mês passado/sem projeção — comportamento atual).
  const linhas = useMemo<DiaLinha[]>(() => {
    const diasProj = projecao.data?.dias ?? [];
    if (isMesCorrente && diasProj.length > 0) {
      // Nos dias realizados o backend devolve o próprio realizado em pld_projetado.
      return diasProj.map((d) => ({
        data: d.data,
        pld: d.pld_projetado,
        projetado: !d.realizado,
      }));
    }
    return (overview.data?.dados_diarios ?? []).map((d) => ({
      data: d.data,
      pld: d.pld_media_dia,
      projetado: false,
    }));
  }, [isMesCorrente, projecao.data, overview.data]);

  if (overview.error) return <PldError />;
  if (overview.isLoading || !overview.data) return <PldLoading />;
  if (overview.data.dados_diarios.length === 0) return <PldEmpty />;
  // No mês corrente esperamos a projeção pra não "piscar" só-realizado → mês completo.
  if (isMesCorrente && projecao.isLoading) return <PldLoading />;

  const semanasList = semanas.data?.semanas ?? [];
  const versao = projecao.data?.modelo_versao ?? "";
  const temProjecao = isMesCorrente && linhas.some((l) => l.projetado);

  return (
    <div data-testid="pld-semanal">
      <p className="text-white/50 text-xs mb-3">
        Dia a dia de {SUBMERCADO_LABEL[submercado]} — PLD diário e a revisão de
        semana operativa ONS a que pertence.
        {temProjecao && (
          <>
            {" "}Dias futuros (em <span style={{ color: COR_PROJECAO }}>âmbar/itálico</span>) ={" "}
            projeção <span className="text-white/70">{versao}</span> (#208).
          </>
        )}
      </p>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Data</th>
              <th className="px-3 py-2 text-left">Dia</th>
              <th className="px-3 py-2 text-right">PLD diário</th>
              <th className="px-3 py-2 text-center">Revisão</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              // Semana (janela) que contém o dia → rótulo + status time-based dela.
              const sem = semanasList.find(
                (s) => l.data >= s.periodo_inicio && l.data <= s.periodo_fim,
              );
              const rev = sem?.revisao ?? "";
              const status = sem?.status;
              const badge = status ? statusBadge(status) : null;
              return (
                <tr
                  key={l.data}
                  className="border-t border-white/5 hover:bg-white/5"
                  data-testid="pld-dia-row"
                >
                  <td className="px-3 py-2 text-white/90">{fmtDateBR(l.data)}</td>
                  <td className="px-3 py-2 text-white/60">{diaSemanaBR(l.data)}</td>
                  <td
                    className={`px-3 py-2 text-right font-mono ${
                      l.projetado ? "italic" : "text-white/90"
                    }`}
                    style={l.projetado ? { color: COR_PROJECAO } : undefined}
                    title={l.projetado ? `Projeção (${versao}) — estimativa` : undefined}
                    data-testid="pld-dia-pld"
                  >
                    {fmtPld(l.pld)}
                  </td>
                  <td className="px-3 py-2 text-center text-white/70">{rev || "—"}</td>
                  <td className="px-3 py-2 text-center" data-testid="pld-dia-status">
                    {badge ? (
                      <span className={`px-2 py-0.5 rounded-full text-[11px] ${badge.className}`}>
                        {badge.label}
                      </span>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
